import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, Mail } from "lucide-react";
import { api, ApiRequestError } from "../api";
import { useI18n } from "../i18n";
import { BrandMark } from "../ui/BrandMark";
import { LanguageSelect } from "../ui/LanguageSelect";

const loginCopy = {
  en: { headline: "Send one link.\nKnow tonight who is ready for tomorrow.", intro: "One clear departure view for guests, gear, pickup, balances, and staff review.", safety: "Dive Ready organizes administrative and operational checks. Final participation decisions remain with qualified center staff.", workspace: "Open your center's departure workspace.", error: "Could not sign in. Please try again." },
  de: { headline: "Einen Link senden.\nHeute wissen, wer morgen bereit ist.", intro: "Eine klare Ausfahrtsansicht für Gäste, Ausrüstung, Abholung, Salden und Teamprüfungen.", safety: "Dive Ready organisiert administrative und betriebliche Prüfungen. Die endgültige Teilnahmeentscheidung bleibt bei qualifizierten Center-Mitarbeitern.", workspace: "Öffnen Sie den Ausfahrts-Arbeitsbereich Ihres Centers.", error: "Anmeldung nicht möglich. Bitte versuchen Sie es erneut." },
  es: { headline: "Envía un enlace.\nSabe hoy quién está listo para mañana.", intro: "Una vista clara para invitados, equipo, recogida, saldos y revisiones del personal.", safety: "Dive Ready organiza comprobaciones administrativas y operativas. La decisión final corresponde al personal cualificado.", workspace: "Abre el espacio de salidas de tu centro.", error: "No se pudo iniciar sesión. Inténtalo de nuevo." },
  fr: { headline: "Envoyez un lien.\nSachez ce soir qui est prêt pour demain.", intro: "Une vue claire pour invités, équipement, transport, soldes et vérifications du personnel.", safety: "Dive Ready organise les contrôles administratifs et opérationnels. La décision finale appartient au personnel qualifié.", workspace: "Ouvrez l'espace de sorties de votre centre.", error: "Connexion impossible. Veuillez réessayer." },
} as const;

export function LoginPage() {
  const { language, t } = useI18n();
  const copy = loginCopy[language];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api("/api/v1/foundation/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      await queryClient.invalidateQueries({ queryKey: ["session"] });
      const state = location.state as { from?: { pathname?: string } } | null;
      navigate(state?.from?.pathname ?? "/", { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : copy.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-brand-panel">
        <BrandMark />
        <div>
          <h1>{copy.headline.split("\n").map((line, index) => <span key={line}>{index > 0 && <br />}{line}</span>)}</h1>
          <p>{copy.intro}</p>
        </div>
        <p className="login-safety">{copy.safety}</p>
      </section>
      <section className="login-form-panel">
        <form className="login-form" onSubmit={submit}>
          <div className="login-language-first"><strong>Choose language · Sprache wählen · Elegir idioma · Choisir la langue</strong><LanguageSelect light /></div>
          <h2>{t("signIn")}</h2>
          <p>{copy.workspace}</p>
          {error && <div className="form-error" role="alert">{error}</div>}
          <label><span>{t("email")}</span><div className="input-with-icon"><Mail size={20} /><input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required /></div></label>
          <label><span>{t("password")}</span><div className="input-with-icon"><KeyRound size={20} /><input type="password" autoComplete="current-password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} required /></div></label>
          <button className="button button-primary button-full" disabled={busy}>{busy ? t("loading") : t("signIn")}</button>
        </form>
      </section>
    </main>
  );
}
